import { Link, useNavigate } from '@tanstack/react-router';
import { Code2 } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { useUpdateProgress } from '@/shared/api/queries/progress';
import { StudioLayout } from '@/shared/layout/studio-layout';
import { activityView } from '@/shared/program/derive';
import { useProgramFull } from '@/shared/program/use-program-full';
import { useRecordStudioVisit } from '@/shared/program/use-studio-resume';
import { ACTIVITY_KIND, activityHref } from '@/shared/ui/activity/activity-kind';
import { ActivityReader } from '@/shared/ui/activity/activity-reader';
import { Alert } from '@/shared/ui/alert';
import { buttonVariants } from '@/shared/ui/button-variants';
import { EmptyState } from '@/shared/ui/empty-state';

export function LessonPage({ activityId }: { activityId: string }) {
  const navigate = useNavigate();
  const program = useProgramFull((data) => ({
    activity: activityView(data, activityId),
    slug: data.slug,
  }));
  const activity = program.data?.activity;
  useRecordStudioVisit(
    !program.error &&
      activity?.id === activityId &&
      (activity.kind === 'lesson' || activity.kind === 'checkpoint') &&
      program.data?.slug
      ? { programSlug: program.data.slug, activityId: activity.id, kind: activity.kind }
      : null,
    { entryId: activityId },
  );
  const progress = useUpdateProgress(activityId);
  const started = useRef<string | null>(null);
  const { mutate } = progress;

  useEffect(() => {
    if (
      activity?.kind !== 'workshop' &&
      activity?.status === 'not_started' &&
      started.current !== activityId
    ) {
      started.current = activityId;
      mutate({ status: 'in_progress' });
    }
  }, [activity?.kind, activity?.status, activityId, mutate]);

  useEffect(() => {
    if (activity?.kind === 'workshop') {
      void navigate({ to: activityHref(activity.kind, activity.id), replace: true });
    }
  }, [activity?.kind, activity?.id, navigate]);

  async function completeAndContinue() {
    if (activity?.kind === 'workshop') return;
    try {
      await progress.mutateAsync({ status: 'complete' });
    } catch {
      return;
    }
    if (activity?.next) await navigate({ to: activityHref(activity.next.kind, activity.next.id) });
  }

  return (
    <StudioLayout
      activeId={activityId}
      subtitle={activity ? `${ACTIVITY_KIND[activity.kind].label} · ${activity.title}` : undefined}
      headerActions={
        activity?.kind !== 'workshop' && activity?.next?.kind === 'workshop' ? (
          <Link
            to="/workshop/$activityId"
            params={{ activityId: activity.next.id }}
            title={`Practice: ${activity.next.title}`}
            className={buttonVariants({ variant: 'secondary', size: 'sm' })}
          >
            <Code2 className="size-4" aria-hidden="true" />
            Practice
          </Link>
        ) : undefined
      }
    >
      {() =>
        !activity ? (
          <div className="p-8">
            <EmptyState
              title="Lesson not found"
              description="This lesson may have moved. Browse the curriculum to find your next activity."
              action={
                <Link to="/studio" className={buttonVariants()}>
                  Back to Studio
                </Link>
              }
            />
          </div>
        ) : activity.kind === 'workshop' ? null : (
          <div className="studio-reader-padding px-5 pb-12 sm:px-10">
            {progress.error ? (
              <div className="mx-auto mt-4 max-w-[780px]">
                <Alert>Could not save your progress. {progress.error.message}</Alert>
              </div>
            ) : null}
            <ActivityReader
              key={activity.id}
              activity={activity}
              completing={progress.isPending}
              onComplete={() => void completeAndContinue()}
              completeLabel={activity.next ? 'Complete lesson & continue' : 'Complete lesson'}
            />
          </div>
        )
      }
    </StudioLayout>
  );
}
